import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { EMPTY, of } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { JobFile } from '@core/models/job-file/job-file.model';
import { JobFileContainer } from '@core/models/job-file/job-file-container.model';
import { JobFileServiceModel } from '@core/models/job-file/job-file-service.model';
import { ShipmentOrderSpec } from '@core/models/job-file/shipment-order-spec.model';
import { BillingInfo } from '@core/models/job-file/billing-info.model';
import { LoadManagementService } from '../services/load-management.service';
import { SetSpinner } from '@core/store/shared.actions';
import { JobFileService } from '@core/services/job-file.service';
import {
  CurrentLocation,
  JobDetailsHeaderViewModel,
  LoadManagementSummaryMetrics,
  SummaryCard,
  UtilMetricSet
} from '../models/load-management-view.model';
import { AddedService, addedServiceKey, LoadManagementStateModel } from '../models/state/load-management-state.model';
import { OperationLabel } from '@core/models/operation-label.model';
import { OperationLabelService } from '@core/services/operation-label.service';
import {
  AddNewContainer,
  AddService,
  ApplyServiceSequenceTemplate,
  BatchSaveServices,
  DeleteService,
  ContainerRemoved,
  DuplicateContainer,
  GetContainer,
  GetVehicleCategories,
  LoadJobFile,
  LoadSavedRoutes,
  RemoveCollaborator,
  RemoveContainer,
  RemoveServiceSequenceTemplate,
  ResetLoadManagementState,
  SaveServiceSequenceTemplate,
  UpdateServiceSequenceTemplate,
  UpdateBillingActivated,
  UpdateLegFreezer,
  UpdateService,
  GetPartnerOrganizations,
  SearchPartnerOrganizations,
  GetVehiclesByOrganization,
  ResetPartnerOrganizationsAndVehicles,
  LoadDigitalTwinVehicles,
  ResetDigitalTwinVehicles,
  LoadDigitalTwinVehiclesByOrg,
  LoadWorkOrderDefinitionsAction,
  LoadActivityDefinitions,
  LoadWorkflowDefinition,
  InviteCollaborators,
  OptimizeContainers,
  UpdateContainerFcl,
  UpdateContainerVehicleCategory,
  UpdateContainerCustomsChannel,
  LoadOperationLabels,
  LoadInvoicedLoadIds,
  UpdateContainerLabels,
  UpdateLoadKeyDetails,
  SaveRemark,
  SaveRemarks,
  DeleteRemark,
  LoadRemarks,
  SaveWaypoints,
  UpdateContainerRouteWaypoints,
  UpdateContainer,
  FindInitContainersByContainerIds,
  SaveOtherReferenceNumber,
  DeleteOtherReferenceNumber
} from './load-management.actions';
import { SetNotificationAction } from '@core/store/notification/notification.actions';
import { AlertDialogType } from '@shared/models';
import { AuthState } from '@core/store/auth/auth.state';
import { ServiceSequenceTemplate } from '../../wizard/models/service-sequence-template.model';
import { AddContainer } from '../../wizard/models/add-container.model';
import { EntityMap } from '@shared/models/types';
import { VehicleCategory } from '@shared/models/vehicle-category.model';
import { BillingIndicator } from '../../wizard/models/enums/billing-indicator-type.enum';
import { WorkOrderService } from '@shared/services/work-order.service';
import { WorkOrderDefinition } from '@control-tower/models/work-order/work-order-definition.model';
import { WorkFlowConfigurationService } from '../../wizard/services/work-flow-configuration.service';

const getInitialState = (): LoadManagementStateModel => ({
  jobFile: null,
  loading: false,
  error: null,
  savedRoutes: [],
  containers: {},
  vehicleCategories: [],
  standardVehicleCategories: [],
  partnerOrganizations: [],
  partnerOrganizationsSearch: [],
  vehiclesByOrganization: {},
  loadingPartnerOrgs: false,
  loadingVehicles: false,
  digitalTwinVehicles: [],
  digitalTwinLoading: false,
  digitalTwinPage: 0,
  digitalTwinTotalPages: 0,
  digitalTwinPageSize: 20,
  activityList: [],
  workflowDefinition: null,
  undoWaypoints: {},
  redoWaypoints: {},
  rootLoads: [],
  addedServices: {},
  operationLabels: [],
  operationLabelsOperationId: null,
  invoicedLoadIds: []
});

@State<LoadManagementStateModel>({
  name: 'loadManagement',
  defaults: getInitialState()
})
@Injectable()
export class LoadManagementState {

  // shipmentOrderSpecs are served from a dedicated endpoint and are no longer embedded in the
  // job-file response. This panel is not paginated — it loads every spec at once via the
  // dedicated non-paginated "/all" endpoint (see hydrateShipmentOrderSpecs).

  constructor(
    private readonly loadManagementService: LoadManagementService,
    private readonly store: Store,
    private readonly workOrderService: WorkOrderService,
    private readonly workFlowConfigurationService: WorkFlowConfigurationService,
    private readonly jobFileService: JobFileService,
    private readonly operationLabelService: OperationLabelService
  ) {}

  @Selector()
  static jobDetails(state: LoadManagementStateModel): JobDetailsHeaderViewModel | null {
    const jobFile = state.jobFile;
    if (!jobFile) {
      return null;
    }

    return {
      workflow: jobFile.workflowId ?? '—',
      jobName: jobFile.jobTitle || jobFile.title || '—',
      jobRef: jobFile.jobRefId,
      title: 'Load Management'
    };
  }

  @Selector()
  static activityList(state: LoadManagementStateModel): WorkOrderDefinition[] {
    return state.activityList;
  }

  @Selector()
  static getWorkflowDefinitionAdditionalActivities(state: LoadManagementStateModel): string[] {
    return state.workflowDefinition?.additionalServices || [];
  }

  @Selector()
  static summaryCards(state: LoadManagementStateModel): SummaryCard[] {
    const metrics = aggregateSummaryMetrics(state.jobFile?.containers ?? []);

    return [
      {
        icon: 'assets/booking-engine/icons/order.svg',
        label: 'Total Orders',
        value: metrics.totalOrders
      },
      {
        icon: 'assets/booking-engine/icons/shipment.svg',
        label: 'Total Shipments',
        value: metrics.totalShipments
      },
      {
        icon: 'assets/booking-engine/icons/location.svg',
        label: 'Total Pickup Locations',
        value: metrics.totalPickupLocations
      },
      {
        icon: 'assets/booking-engine/icons/location.svg',
        label: 'Total Delivery Locations',
        value: metrics.totalDeliveryLocations
      },
      {
        icon: 'assets/booking-engine/icons/shipment.svg',
        label: 'Total Truck Loads',
        value: metrics.totalTruckLoads
      },
      {
        icon: '',
        label: 'Total Planned Distance',
        value: `${formatDecimal(metrics.totalPlannedDistanceKm, 2)} Km`
      }
    ];
  }

  @Selector()
  static utilMetricSets(state: LoadManagementStateModel): UtilMetricSet[] {
    const metrics = aggregateSummaryMetrics(state.jobFile?.containers ?? []);
    const capacityUtilization = safePercentage(metrics.carryingCapacity, metrics.plannedCapacity);
    const weightUtilization = safePercentage(metrics.carryingWeight, metrics.plannedWeight);

    return [
      {
        key: 'weight',
        label: 'Total Weight',
        plannedLabel: 'Planned Weight',
        utilization: weightUtilization,
        planned: `${formatDecimal(metrics.plannedWeight, 1)} Kg`,
        available: `${formatDecimal(Math.max(metrics.plannedWeight - metrics.carryingWeight, 0), 1)} Kg`,
        availableLabel: 'Available Weight',
        color: getUtilizationColor(weightUtilization)
      },
      {
        key: 'capacity',
        label: 'Capacity',
        plannedLabel: 'Planned Capacity',
        utilization: capacityUtilization,
        planned: `${formatDecimal(metrics.plannedCapacity, 2)} CBM`,
        available: `${formatDecimal(Math.max(metrics.plannedCapacity - metrics.carryingCapacity, 0), 2)} CBM`,
        availableLabel: 'Available Capacity',
        color: getUtilizationColor(capacityUtilization)
      }
    ];
  }


  @Selector()
  static isLoading(state: LoadManagementStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static error(state: LoadManagementStateModel): string | null {
    return state.error;
  }

  @Selector()
  static jobFile(state: LoadManagementStateModel): JobFile | null {
    return state.jobFile;
  }

  @Selector()
  static operationLabels(state: LoadManagementStateModel): OperationLabel[] {
    return state.operationLabels;
  }

  @Selector()
  static invoicedLoadIds(state: LoadManagementStateModel): string[] {
    return state.invoicedLoadIds;
  }

  /**
   * Services created by AddService POSTs, keyed by addedServiceKey(containerId, index) and carrying
   * the server-generated id. Read right after the dispatch completes so a newly added activity knows
   * its id without waiting for the LoadJobFile refetch.
   */
  @Selector()
  static addedServices(state: LoadManagementStateModel): Record<string, AddedService> {
    return state.addedServices;
  }

  @Selector()
  static getVehicleCategories(state: LoadManagementStateModel): VehicleCategory[] {
    return state.vehicleCategories;
  }

  @Selector()
  static getStandardVehicleCategories(state: LoadManagementStateModel): VehicleCategory[] {
    return state.standardVehicleCategories;
  }

  @Selector()
  static partnerOrganizations(state: LoadManagementStateModel) {
    return state.partnerOrganizations;
  }

  @Selector()
  static partnerOrganizationsSearch(state: LoadManagementStateModel) {
    return state.partnerOrganizationsSearch;
  }

  @Selector()
  static vehiclesByOrganization(state: LoadManagementStateModel) {
    return state.vehiclesByOrganization;
  }

  @Selector()
  static loadingPartnerOrgs(state: LoadManagementStateModel): boolean {
    return state.loadingPartnerOrgs;
  }

  @Selector()
  static loadingVehicles(state: LoadManagementStateModel): boolean {
    return state.loadingVehicles;
  }

  @Selector()
  static digitalTwinVehicles(state: LoadManagementStateModel) {
    return state.digitalTwinVehicles;
  }

  @Selector()
  static isDigitalTwinLoading(state: LoadManagementStateModel): boolean {
    return state.digitalTwinLoading;
  }

  @Selector()
  static digitalTwinPage(state: LoadManagementStateModel): number {
    return state.digitalTwinPage;
  }

  @Selector()
  static digitalTwinTotalPages(state: LoadManagementStateModel): number {
    return state.digitalTwinTotalPages;
  }

  @Selector()
  static digitalTwinPageSize(state: LoadManagementStateModel): number {
    return state.digitalTwinPageSize;
  }

  @Selector()
  static rootLoads(state: LoadManagementStateModel): JobFileContainer[] {
    return state.rootLoads;
  }

  @Action(LoadJobFile)
  loadData(
    ctx: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId }: LoadJobFile
  ) {
    ctx.patchState({
      loading: true,
      error: null
    });

    return this.loadManagementService.getJobFile(orgId, jobRefId).pipe(
      switchMap((jobFileFromMainJobRaw: JobFile) =>
        this.hydrateShipmentOrderSpecs(jobFileFromMainJobRaw, orgId, jobRefId)
      ),
      switchMap((jobFileFromMainJob: JobFile) => {
        // "size" is not a standard property or method on JS Map without using size.
        // For Map, use .size. For a plain object, use Object.keys().length.
        const hasChain =
          jobFileFromMainJob?.jobFileChain &&
          (
            jobFileFromMainJob.jobFileChain instanceof Map
              ? jobFileFromMainJob.jobFileChain.size >= 1
              : Object.keys(jobFileFromMainJob.jobFileChain).length >= 1
          );
        if (!hasChain) {
          ctx.patchState({
            jobFile: jobFileFromMainJob,
            loading: false,
            error: null
          });
          return of(null);
        }
        const chainEntry = Object.values(jobFileFromMainJob.jobFileChain)[0];
        const jobRefIdOfMainJob = chainEntry?.jobRefId;
        const orgIdOfMainJob = chainEntry?.orgId;
        if (!jobRefIdOfMainJob || !orgIdOfMainJob) {
          ctx.patchState({
            jobFile: jobFileFromMainJob,
            loading: false,
            error: null
          });
          return of(null);
        }
        return this.loadManagementService.getJobFile(orgIdOfMainJob, jobRefIdOfMainJob).pipe(
          switchMap((jobFileFromParentJobRaw: JobFile) =>
            this.hydrateShipmentOrderSpecs(jobFileFromParentJobRaw, orgIdOfMainJob, jobRefIdOfMainJob)
          ),
          tap((jobFileFromParentJob: JobFile) => {
            // ALWAYS keep the MAIN (navigated) job's identity — jobRefId/orgId — because the
            // container list rendered on this page belongs to the main job, and every mutation
            // (optimize/updateOrdersData/delete/duplicate) uses jobFile.jobRefId for the request
            // path. The parent job is fetched ONLY to borrow its container objects for a chained
            // load; substitute those in when present, but never adopt the parent's jobRefId (doing
            // so pairs a main-job containerId with the parent's jobRefId → cross-job request → the
            // backend rejects with "Container not found with Id" 400).
            let resolvedJobFile: JobFile = jobFileFromMainJob;
            if (jobFileFromParentJob?.containers?.length > 0 && jobFileFromParentJob?.loadReferences?.length > 0) {
              const containerIds: string[] = jobFileFromMainJob.loadReferences.map((reference: any) => reference.loadId);
              const filteredContainers = jobFileFromParentJob.containers.filter((container: JobFileContainer) =>
                containerIds.includes(container.containerId)
              );
              resolvedJobFile = { ...jobFileFromMainJob, containers: filteredContainers };
            }
            ctx.patchState({
              jobFile: resolvedJobFile,
              loading: false,
              error: null
            });
          }),
          catchError(error => {
            ctx.patchState({
              loading: false,
              error: error?.message ?? 'Failed to load job data'
            });
            return EMPTY;
          })
        );
      }),
      catchError(error => {
        ctx.patchState({
          loading: false,
          error: error?.message ?? 'Failed to load job data'
        });
        return EMPTY;
      }),
      finalize(() => ctx.patchState({ loading: false }))
    );
  }

  /**
   * The manual-plan order panel (job-plan-order-list) now fetches its shipment order specs itself,
   * server-side paginated, via jobFileService.getShipmentOrderSpecsPanel. So we intentionally do NOT
   * hydrate the full specs list onto jobFile anymore — loading every spec froze the browser on large
   * jobs. jobFile still carries containers etc.; container assigned-orders live under
   * container.shipmentLoadDetails and are unaffected.
   */
  private hydrateShipmentOrderSpecs(jobFile: JobFile, orgId: string, jobRefId: string) {
    return of(jobFile);
  }

  @Action(ResetLoadManagementState)
  resetState(ctx: StateContext<LoadManagementStateModel>) {
    ctx.setState(getInitialState());
  }

  @Action(AddNewContainer)
  addNewContainer(
    { dispatch, getState }: StateContext<LoadManagementStateModel>,
    { vehicleData }: AddNewContainer
  ) {

    const jobFile = getState().jobFile;

    if (!jobFile?.jobRefId) {
      return dispatch(new SetNotificationAction('Unable to add container: Missing job reference', AlertDialogType.ERROR));
    }

    return this.loadManagementService.addNewContainer(jobFile.orgId, jobFile.jobRefId, vehicleData)
      .pipe(
        tap((containers: JobFileContainer[]) => {
          dispatch(new LoadJobFile(jobFile.orgId, jobFile.jobRefId));
          dispatch(new SetNotificationAction('New container added successfully', AlertDialogType.SUCCESS));
        }),
        catchError(error => {
          dispatch(new SetNotificationAction('Failed to add new container', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(AddService)
  addService(
    { dispatch, patchState, getState }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, rootOrgId, rootJobRefId, containerId, index, service, skipReload }: AddService
  ) {
    return this.loadManagementService.addService(rootOrgId, rootJobRefId, containerId, index, service)
      .pipe(
        tap((addedService) => {
          // Keep the created service: it is the only immediate source of the server-generated id.
          // The LoadJobFile below is the slow path (a full job-file refetch, twice for chained jobs),
          // and until it lands the caller would still consider the row unsaved and add it again.
          if (addedService) {
            patchState({
              addedServices: {
                ...getState().addedServices,
                [addedServiceKey(containerId, index)]: { containerId, index, service: addedService }
              }
            });
          }
          dispatch(new SetNotificationAction('Service added successfully', AlertDialogType.SUCCESS));
          // Batch save (route-setup-dialog) reloads the job file once after every row is saved -
          // skip the per-row reload so a large batch doesn't queue a LoadJobFile per row.
          if (!skipReload) {
            dispatch(new LoadJobFile(orgId, jobRefId));
          }
        }),
        finalize(() => {
        }),
        catchError((error) => {
          dispatch(new SetNotificationAction('Failed to add service', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(UpdateService)
  updateService(
    { dispatch }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, rootOrgId, rootJobRefId, containerId, service, skipReload }: UpdateService
  ) {
    return this.loadManagementService.updateService(rootOrgId, rootJobRefId, containerId, service)
      .pipe(
        tap((updatedService) => {
          dispatch(new SetNotificationAction('Service saved successfully', AlertDialogType.SUCCESS));
          // Batch save (route-setup-dialog) reloads the job file and container once after every row
          // is saved - skip the per-row reload so a large batch doesn't queue a LoadJobFile/GetContainer
          // per row and starve the connection pool the next row's save needs.
          if (!skipReload) {
            dispatch(new LoadJobFile(orgId, jobRefId));
            dispatch(new GetContainer(rootOrgId, rootJobRefId, containerId));
          }
        }),
        finalize(() => {
        }),
        catchError((error) => {
          dispatch(new SetNotificationAction('Failed to save service', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(BatchSaveServices)
  batchSaveServices(
    { dispatch, patchState, getState }: StateContext<LoadManagementStateModel>,
    { rootOrgId, rootJobRefId, containerId, items, sessionId, useCache }: BatchSaveServices
  ) {
    if (!useCache && (!items || items.length === 0)) {
      return EMPTY;
    }

    return this.loadManagementService.batchSaveServices(rootOrgId, rootJobRefId, containerId, items, sessionId, useCache)
      .pipe(
        tap((response) => {
          const results = response.items || [];
          const failed = results.filter(result => result.status === 'FAILED');
          if (failed.length > 0) {
            dispatch(new SetNotificationAction(
              `${failed.length} of ${results.length} service(s) failed to save`,
              AlertDialogType.ERROR
            ));
          } else {
            dispatch(new SetNotificationAction('Services saved successfully', AlertDialogType.SUCCESS));
          }

          // Patch the container locally from the response instead of a separate GetContainer
          // round trip - applyServiceBatchOptimized already computed all of this in memory.
          // Merge (not replace) so fields the batch-save response doesn't carry (customer,
          // typeAndSize, vehicleCategory, etc.) survive untouched.
          const containers = { ...getState().containers };
          const existing = containers[containerId] || ({} as AddContainer);
          containers[containerId] = {
            ...existing,
            services: response.services,
            legs: response.legs,
            billableEstimatedTime: response.billableEstimatedTime,
            distanceBetweenStartAndEnd: response.distanceBetweenStartAndEnd,
            useExpressWay: response.useExpressWay
          };
          patchState({ containers });
        }),
        catchError((error) => {
          dispatch(new SetNotificationAction('Failed to save services', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(DeleteService)
  deleteService(
    { dispatch }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, containerId, serviceId }: DeleteService
  ) {
    dispatch(new SetSpinner(true));

    return this.loadManagementService.removeService(orgId, jobRefId, containerId, serviceId)
      .pipe(
        tap((jobFile) => {
          dispatch(new SetNotificationAction('Service deleted successfully', AlertDialogType.SUCCESS));
          dispatch(new LoadJobFile(orgId, jobRefId));
          dispatch(new GetContainer(orgId, jobRefId, containerId));
        }),
        finalize(() => {
          dispatch(new SetSpinner(false));
        }),
        catchError((error) => {
          dispatch(new SetNotificationAction('Error deleting service', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(UpdateBillingActivated)
  updateBillingActivated(
    { dispatch }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, containerId, service, billingActivated }: UpdateBillingActivated
  ) {
    if (!service?.id) {
      dispatch(new SetNotificationAction('Service ID is required to update billing status', AlertDialogType.ERROR));
      return EMPTY;
    }

    // Determine billing indicator - use existing if available, otherwise use BILLING_START when activating
    let billingIndicator: BillingIndicator;
    if (service.billingIndicatorType) {
      // Extract billing indicator from service (handle both object and string/enum cases)
      const existingIndicator = typeof service.billingIndicatorType === 'object' 
        ? (service.billingIndicatorType as any)?.id || (service.billingIndicatorType as any)?.name || (service.billingIndicatorType as any)?.value
        : service.billingIndicatorType;
      
      billingIndicator = existingIndicator === BillingIndicator.BILLING_END 
        ? BillingIndicator.BILLING_END 
        : BillingIndicator.BILLING_START;
    } else {
      // Default to BILLING_START when activating billing
      billingIndicator = BillingIndicator.BILLING_START;
    }

    const billingInfo: BillingInfo = {
      billingIndicator,
      active: billingActivated
    };

    return this.loadManagementService.setBilling(orgId, jobRefId, containerId, service.id, billingInfo)
      .pipe(
        tap((updatedServiceResult) => {
          dispatch(new LoadJobFile(orgId, jobRefId));
          dispatch(new GetContainer(orgId, jobRefId, containerId));
        }),
        finalize(() => {
        }),
        catchError((error) => {
          dispatch(new SetNotificationAction('Error updating billing status', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(UpdateLegFreezer)
  updateLegFreezer(
    { dispatch }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, containerId, legIndex, useFreezer }: UpdateLegFreezer
  ) {
    return this.loadManagementService.setLegFreezer(orgId, jobRefId, containerId, legIndex, useFreezer)
      .pipe(
        tap(() => {
          dispatch(new LoadJobFile(orgId, jobRefId));
          dispatch(new GetContainer(orgId, jobRefId, containerId));
        }),
        catchError((error) => {
          dispatch(new SetNotificationAction('Error updating freezer status', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Selector()
  static savedRoutes(state: LoadManagementStateModel): ServiceSequenceTemplate[] {
    return state.savedRoutes;
  }

  @Action(LoadSavedRoutes)
  loadSavedRoutes(
    { dispatch, patchState }: StateContext<LoadManagementStateModel>,
    { keyword }: LoadSavedRoutes
  ) {
    const orgId = this.store.selectSnapshot(AuthState.getOrgId);
    const jobFile = this.store.selectSnapshot(LoadManagementState.jobFile);
    
    if (!orgId || !jobFile?.jobRefId || !jobFile?.operationId) {
      return EMPTY;
    }

    return this.loadManagementService.getSavedRoutes(jobFile.jobRefId, orgId, jobFile.operationId, keyword)
      .pipe(
        map((savedRoutes) => patchState({ savedRoutes })),
        catchError((error) => {
          dispatch(new SetNotificationAction('Error loading saved routes', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(SaveServiceSequenceTemplate)
  saveServiceSequenceTemplate(
    { dispatch, getState, patchState }: StateContext<LoadManagementStateModel>,
    { containerId, name }: SaveServiceSequenceTemplate
  ) {
    const orgId = this.store.selectSnapshot(AuthState.getOrgId);
    const jobFile = this.store.selectSnapshot(LoadManagementState.jobFile);
    
    if (!orgId || !jobFile?.jobRefId || !jobFile?.operationId) {
      dispatch(new SetNotificationAction('Missing required information to save route', AlertDialogType.ERROR));
      return EMPTY;
    }

    const normalizeTemplateName = (templateName: string): string => {
      if (!templateName) return '';
      return templateName.trim().replace(/\s+/g, ' ').toUpperCase();
    };

    const normalizedName = normalizeTemplateName(name);

    const template: ServiceSequenceTemplate = {
      id: undefined,
      templateName: normalizedName,
      containerId: containerId,
      orgId: orgId,
      operationId: jobFile.operationId,
      jobRefId: jobFile.jobRefId,
      templateId: ''
    };

    return this.loadManagementService.saveServiceSequenceTemplate(template)
      .pipe(
        tap((savedTemplate) => {
          const alreadySavedRoutes = getState().savedRoutes;
          patchState({ savedRoutes: [...alreadySavedRoutes, savedTemplate] });
          dispatch(new SetNotificationAction('Route saved successfully', AlertDialogType.SUCCESS));
        }),
        catchError((error) => {
          dispatch(new SetNotificationAction('Failed to save route', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(UpdateServiceSequenceTemplate)
  updateServiceSequenceTemplate(
    { dispatch, getState, patchState }: StateContext<LoadManagementStateModel>,
    { containerId, templateId, name }: UpdateServiceSequenceTemplate
  ) {
    const orgId = this.store.selectSnapshot(AuthState.getOrgId);
    const jobFile = this.store.selectSnapshot(LoadManagementState.jobFile);
    
    if (!orgId || !jobFile?.jobRefId || !jobFile?.operationId) {
      dispatch(new SetNotificationAction('Missing required information to update route', AlertDialogType.ERROR));
      return EMPTY;
    }

    const normalizeTemplateName = (templateName: string): string => {
      if (!templateName) return '';
      return templateName.trim().replace(/\s+/g, ' ').toUpperCase();
    };

    const template: ServiceSequenceTemplate = {
      id: templateId,
      templateName: normalizeTemplateName(name),
      containerId: containerId,
      orgId: orgId,
      operationId: jobFile.operationId,
      jobRefId: jobFile.jobRefId,
      templateId: templateId
    };

    return this.loadManagementService.updateServiceSequenceTemplate(template)
      .pipe(
        tap((updatedTemplate) => {
          const updatedRoutes = getState().savedRoutes.map(route => 
            route.id === templateId ? updatedTemplate : route
          );
          patchState({ savedRoutes: updatedRoutes });
          dispatch(new SetNotificationAction('Route updated successfully', AlertDialogType.SUCCESS));
        }),
        catchError(() => {
          dispatch(new SetNotificationAction('Failed to update route', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(ApplyServiceSequenceTemplate)
  applyServiceSequenceTemplate(
    { dispatch }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, containerId, templateId }: ApplyServiceSequenceTemplate
  ) {
    return this.loadManagementService.applyServiceSequenceTemplate(orgId, jobRefId, containerId, templateId)
      .pipe(
        map(() => {
          return dispatch(new SetNotificationAction('Route applied successfully', AlertDialogType.SUCCESS));
        }),
        catchError(() => {
          return dispatch(new SetNotificationAction('Failed to apply route', AlertDialogType.ERROR));
        })
      );
  }

  @Action(RemoveServiceSequenceTemplate)
  removeServiceSequenceTemplate(
    { dispatch, getState, patchState }: StateContext<LoadManagementStateModel>,
    { templateId }: RemoveServiceSequenceTemplate
  ) {
    return this.loadManagementService.removeServiceSequenceTemplate(templateId)
      .pipe(
        tap(() => {
          const template = getState().savedRoutes.find(route => route.id === templateId);
          if (template) {
            const updatedRoutes = getState().savedRoutes.filter(route => route.id !== templateId);
            patchState({ savedRoutes: updatedRoutes });
            dispatch(new SetNotificationAction('Route removed successfully', AlertDialogType.SUCCESS));
          }
        }),
        catchError((error) => {
          dispatch(new SetNotificationAction('Failed to remove route', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(RemoveContainer)
  removeContainer(
    { dispatch }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, containerId }: RemoveContainer
  ) {
    return this.loadManagementService.removeContainer(orgId, jobRefId, containerId, true)
      .pipe(
        tap(() => {
          dispatch(new LoadJobFile(orgId, jobRefId));
          dispatch(new SetNotificationAction('Container removed successfully', AlertDialogType.SUCCESS));
        }),
        catchError((error) => {
          dispatch(new SetNotificationAction('Failed to remove container', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(DuplicateContainer)
  duplicateContainer(
    { dispatch }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, containerId }: DuplicateContainer
  ) {
    return this.loadManagementService.duplicateContainer(orgId, jobRefId, containerId)
      .pipe(
        tap(() => {
          dispatch(new LoadJobFile(orgId, jobRefId));
          dispatch(new SetNotificationAction('Container duplicated successfully', AlertDialogType.SUCCESS));
        }),
        catchError((error) => {
          dispatch(new SetNotificationAction('Failed to duplicate container', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Selector([LoadManagementState])
  static container(state: LoadManagementStateModel): EntityMap<string, AddContainer> {
    return state.containers;
  }

  @Action(GetContainer)
  getContainer(
    { dispatch, patchState, getState }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, containerId }: GetContainer
  ) {

    return this.loadManagementService.getContainer(containerId, jobRefId, orgId)
      .pipe(
        map((container) => {
          const containers = { ...getState().containers };
          containers[container.containerId] = container;
          patchState({ containers });
          return container;
        }),
        catchError((error) => {
          // 404 means the container is gone, not that the read failed. Deleting the last unloading
          // activity of a distribution load removes the whole load server-side, so the refresh that
          // follows the delete asks for a container that no longer exists. That is the intended
          // outcome - drop it from the store and let listeners tear down, without an error toast.
          if (error?.status === 404) {
            const containers = { ...getState().containers };
            delete containers[containerId];
            patchState({ containers });
            dispatch(new ContainerRemoved(containerId));
            return EMPTY;
          }
          dispatch(new SetNotificationAction('Failed to get container', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(GetVehicleCategories)
  getVehicleCategories(
    { dispatch, patchState, getState }: StateContext<LoadManagementStateModel>,
    { isStandard, loadType }: GetVehicleCategories
  ) {
    const state = getState();
    const jobFile = state.jobFile;
    const actualLoadType = loadType || jobFile?.loadType;

    // The page loads the non-standard categories on init, then every dialog that needs them
    // re-dispatches this and refetches the same near-static list. Serve what is already in state.
    // Scoped to the non-standard slot on purpose: the standard slot is legitimately re-populated
    // with a different source once the job file's loadType is known (FCL -> containerTypeAndSize).
    if (!isStandard && state.vehicleCategories?.length > 0) {
      return EMPTY;
    }

    // If FCL load type and requesting standard vehicle categories, load from containerTypeAndSize.json
    if (actualLoadType === 'FCL' && isStandard) {
      return this.loadManagementService.getContainerTypeAndSize()
        .pipe(
          map(containerTypes => this.loadManagementService.transformContainerTypesToVehicleCategories(containerTypes)),
          tap((vehicleCategories) => {
            patchState({ standardVehicleCategories: vehicleCategories });
          }),
          catchError(err => {
            dispatch(new SetNotificationAction('Failed to fetch container types', AlertDialogType.ERROR));
            return EMPTY;
          })
        );
    }
    
    // Otherwise, use the standard API endpoint
    return this.loadManagementService.getVehicleCategories(isStandard)
      .pipe(
        tap((vehicleCategories) => {
          if (isStandard) {
            patchState({ standardVehicleCategories: vehicleCategories });
          } else {
            patchState({ vehicleCategories });
          }
        }),
        catchError(err => {
          dispatch(new SetNotificationAction('Failed to fetch vehicle categories', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(GetPartnerOrganizations)
  getPartnerOrganizations(
    { dispatch, patchState, getState }: StateContext<LoadManagementStateModel>,
    { searchKey, pageNum, pageSize }: GetPartnerOrganizations
  ) {
    patchState({ loadingPartnerOrgs: true });

    return this.loadManagementService.getPartnerOrganizations(searchKey, pageNum, pageSize)
      .pipe(
        tap((response) => {
          const authOrgId = this.store.selectSnapshot(AuthState.getOrgId);
          const partnerOrgs = response.content || [];
          
          patchState({ 
            partnerOrganizations: partnerOrgs,
            loadingPartnerOrgs: false
          });
          
          const orgNameMap = new Map<string, string>();
          partnerOrgs.forEach(partner => {
            if (partner.id && partner.orgName) {
              orgNameMap.set(partner.id, partner.orgName);
            }
          });
          
          const existingVehicles = getState().digitalTwinVehicles;
          if (existingVehicles && existingVehicles.length > 0) {
            const updatedVehicles = existingVehicles.map(v => {
              const orgName = orgNameMap.get(v.managedByOrg || '') || v.supplierName;
              const isInternal = v.managedByOrg === authOrgId;
              
              return {
                ...v,
                supplier: orgName,
                supplierName: orgName,
                consignee: orgName,
                ownership: isInternal ? 'INTERNAL' as const : 'EXTERNAL' as const
              };
            });
            
            patchState({ digitalTwinVehicles: updatedVehicles });
          }
        }),
        catchError(err => {
          patchState({ loadingPartnerOrgs: false });
          dispatch(new SetNotificationAction('Failed to fetch partner organizations', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(SearchPartnerOrganizations)
  searchPartnerOrganizations(
    { dispatch, patchState }: StateContext<LoadManagementStateModel>,
    { searchKey, pageNum, pageSize }: SearchPartnerOrganizations
  ) {
    patchState({ loadingPartnerOrgs: true });

    return this.loadManagementService.searchPartnerOrganizations(searchKey, pageNum, pageSize)
      .pipe(
        tap((response) => {
          patchState({ 
            partnerOrganizationsSearch: response.content || [],
            loadingPartnerOrgs: false
          });
        }),
        catchError(err => {
          patchState({ loadingPartnerOrgs: false });
          dispatch(new SetNotificationAction('Failed to search partner organizations', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(GetVehiclesByOrganization)
  getVehiclesByOrganization(
    { dispatch, patchState, getState }: StateContext<LoadManagementStateModel>,
    { orgId }: GetVehiclesByOrganization
  ) {
    patchState({ loadingVehicles: true });

    return this.loadManagementService.getVehiclesByOrganization(orgId)
      .pipe(
        tap((vehicles) => {
          const vehiclesByOrganization = { ...getState().vehiclesByOrganization };
          vehiclesByOrganization[orgId] = vehicles;
          patchState({ 
            vehiclesByOrganization,
            loadingVehicles: false
          });
        }),
        catchError(err => {
          patchState({ loadingVehicles: false });
          dispatch(new SetNotificationAction('Failed to fetch vehicles', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(ResetPartnerOrganizationsAndVehicles)
  resetPartnerOrganizationsAndVehicles({ patchState }: StateContext<LoadManagementStateModel>) {
    patchState({
      partnerOrganizations: [],
      partnerOrganizationsSearch: [],
      vehiclesByOrganization: {},
      loadingPartnerOrgs: false,
      loadingVehicles: false
    });
  }

  @Action(LoadDigitalTwinVehicles)
  loadDigitalTwinVehicles(
    { getState, patchState, dispatch }: StateContext<LoadManagementStateModel>,
    { payload }: LoadDigitalTwinVehicles
  ) {
    const page = payload.page ?? 0;
    const size = payload.size ?? getState().digitalTwinPageSize ?? 20;
    
    // Reset vehicles if explicitly requested
    if (payload.reset) {
      patchState({ digitalTwinVehicles: [] });
    }
    
    patchState({ digitalTwinLoading: true });

    return this.loadManagementService.getDigitalTwinVehicles(payload, page, size)
      .pipe(
        tap((response: any) => {
          const newVehicles = Array.isArray(response) ? response : (response?.content ?? []);
          const { digitalTwinVehicles } = getState();
          
          // Always append new vehicles to existing ones (for lazy loading by organization)
          // unless reset was explicitly requested
          const merged = payload.reset ? newVehicles : [...(digitalTwinVehicles || []), ...newVehicles];
          
          console.log(`Loaded ${newVehicles.length} vehicles, total now: ${merged.length}`);
          
          patchState({
            digitalTwinVehicles: merged,
            digitalTwinLoading: false,
            digitalTwinPage: response?.number ?? page,
            digitalTwinTotalPages: response?.totalPages ?? (page + 1),
            digitalTwinPageSize: response?.size ?? size
          });
        }),
        catchError(err => {
          patchState({ digitalTwinLoading: false });
          return dispatch(new SetNotificationAction('Failed to load digital twin vehicles', AlertDialogType.ERROR));
        })
      );
  }

  @Action(ResetDigitalTwinVehicles)
  resetDigitalTwinVehicles({ patchState }: StateContext<LoadManagementStateModel>) {
    patchState({
      digitalTwinVehicles: [],
      digitalTwinLoading: false,
      digitalTwinPage: 0,
      digitalTwinTotalPages: 0,
      digitalTwinPageSize: 20
    });
  }

  @Action(LoadDigitalTwinVehiclesByOrg)
  loadDigitalTwinVehiclesByOrg(
    { getState, patchState, dispatch }: StateContext<LoadManagementStateModel>,
    { payload }: LoadDigitalTwinVehiclesByOrg
  ) {
    patchState({ digitalTwinLoading: true });

    // The slim endpoint carries each group's orgName, so the organization lookup that used to run
    // sequentially after this call (and blocked the vehicle list from rendering) is gone. The fallback
    // below only fires if the backend has not been deployed yet.
    return this.loadManagementService.getDigitalTwinVehiclesByOrgSlim(payload)
      .pipe(
        mergeMap((response: any) => {
          const groups: any[] = Array.isArray(response) ? response : [];

          const orgNameMap = new Map<string, string>();
          groups.forEach(group => {
            if (group?.orgId && group?.orgName) {
              orgNameMap.set(group.orgId, group.orgName);
            }
          });

          const idsMissingName = groups
            .map(group => group?.orgId)
            .filter(orgId => !!orgId && !orgNameMap.has(orgId));

          if (idsMissingName.length === 0) {
            this.applyDigitalTwinVehicles({ getState, patchState }, groups, orgNameMap);
            return EMPTY;
          }

          // Backward compatibility for a webapp-before-backend deploy: resolve just the names the
          // response did not carry, rather than falling back to placeholder labels. If it fails, the
          // vehicles still render with the placeholder label.
          return this.loadManagementService.getOrganizationsByIds(idsMissingName).pipe(
            catchError(() => of([] as any[])),
            tap((orgs: any[]) => {
              (orgs || []).forEach(org => {
                if (org?.id && org?.orgName) {
                  orgNameMap.set(org.id, org.orgName);
                }
              });
              this.applyDigitalTwinVehicles({ getState, patchState }, groups, orgNameMap);
            })
          );
        }),
        catchError(err => {
          patchState({ digitalTwinLoading: false });
          return dispatch(new SetNotificationAction('Failed to load digital twin vehicles', AlertDialogType.ERROR));
        })
      );
  }

  /**
   * Flattens the org-grouped digital-twin response into the vehicle list the dialog renders and merges
   * the organizations it referenced into `partnerOrganizations` (the filter dialog's partner dropdown).
   *
   * Extracted because the transform used to exist twice, verbatim, in the success and error branches of
   * the old organization-lookup chain.
   */
  private applyDigitalTwinVehicles(
    { getState, patchState }: Pick<StateContext<LoadManagementStateModel>, 'getState' | 'patchState'>,
    groups: any[],
    orgNameMap: Map<string, string>
  ): void {
    const authOrgId = this.store.selectSnapshot(AuthState.getOrgId);

    const existingPartnerOrgs = getState().partnerOrganizations || [];
    const existingOrgIds = new Set(existingPartnerOrgs.map(o => o.id));
    const mergedOrgs = [...existingPartnerOrgs];
    orgNameMap.forEach((orgName, orgId) => {
      if (!existingOrgIds.has(orgId)) {
        mergedOrgs.push({ id: orgId, orgName });
        existingOrgIds.add(orgId);
      }
    });

    const allVehicles: any[] = [];
    groups.forEach((orgGroup: any) => {
      const orgId = orgGroup?.orgId;
      const finalOrgName = orgNameMap.get(orgId) || `Organization (${orgId?.slice(-8) || 'Unknown'})`;
      const vehicles = orgGroup?.vehicles || [];

      vehicles.forEach((v: any) => {
        const managedByOrgId = v?.managedByOrg ?? v?.managedBy ?? orgId;
        const ownedByOrgId = v?.ownedByOrg ?? v?.ownedBy;

        // Determine if vehicle is internal or external
        const isInternal = managedByOrgId === authOrgId;

        allVehicles.push({
          id: String(v?._id ?? v?.id ?? v?.vehicleNumber ?? ''),
          vehicleNumber: String(v?.vehicleNumber ?? v?._id ?? ''),
          categoryName: String(v?.tier?.name ?? v?.type ?? 'Unknown'),
          categoryId: v?.vehicleCategory ?? v?.tier?._id,
          maxCapacityCbm: Number(v?.maxLoadVolume ?? 0),
          maxWeightKg: Number(v?.maxLoadWeight ?? 0),
          status: 'Available' as const,
          utilizationPct: 100,
          supplier: finalOrgName,
          supplierName: finalOrgName,
          vehicleType: v?.type ?? '',
          ownership: isInternal ? 'INTERNAL' as const : 'EXTERNAL' as const,
          consignee: finalOrgName,
          managedByOrg: managedByOrgId,
          ownedByOrg: ownedByOrgId
        });
      });
    });

    patchState({
      partnerOrganizations: mergedOrgs,
      digitalTwinVehicles: allVehicles,
      digitalTwinLoading: false
    });
  }

  @Action(LoadWorkOrderDefinitionsAction)
  loadWorkOrderDefinitions(
    { dispatch, patchState }: StateContext<LoadManagementStateModel>,
    { workOrderIds }: LoadWorkOrderDefinitionsAction
  ) {
    return this.workOrderService.getWorkOrderDefinitionList(workOrderIds)
      .pipe(
        
        tap((activityList) => {
          patchState({
            activityList: activityList
          });
        }),
        catchError(err => {
          dispatch(new SetNotificationAction('Error loading Work Order Definitions', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(LoadActivityDefinitions)
  loadActivityDefinitions(ctx: StateContext<LoadManagementStateModel>) {
    return this.workFlowConfigurationService.getActivityList().pipe(
      tap((activities: WorkOrderDefinition[]) => {
        ctx.patchState({
          activityList: activities
        });
      }),
      catchError(error => {
        ctx.dispatch(new SetNotificationAction('Error loading activity definitions', AlertDialogType.ERROR));
        return EMPTY;
      })
    );
  }

  @Action(LoadWorkflowDefinition)
  loadWorkflowDefinition(
    { patchState, dispatch }: StateContext<LoadManagementStateModel>,
    { workflowId }: LoadWorkflowDefinition
  ) {
    return this.workFlowConfigurationService.getWorkflowDefinitionV2(workflowId)
      .pipe(
        tap((workflowDefinition) => {
          patchState({
            workflowDefinition
          });
        }),
        catchError(err => {
          dispatch(new SetNotificationAction('Error loading workflow definition', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(InviteCollaborators)
  inviteCollaborators(ctx: StateContext<LoadManagementStateModel>, { collaboratorsInvitationModel }: InviteCollaborators) {
    return this.loadManagementService.inviteCollaborators(collaboratorsInvitationModel).pipe(
      tap(() => {
        this.store.dispatch(new LoadJobFile(collaboratorsInvitationModel.orgId, collaboratorsInvitationModel.jobRefId));
        ctx.dispatch(new SetNotificationAction('Collaborators invited successfully', AlertDialogType.SUCCESS));
      }),
      catchError(error => {
        ctx.dispatch(new SetNotificationAction('Failed to invite collaborators', AlertDialogType.ERROR));
        return EMPTY;
      })
    );
  }

  @Action(RemoveCollaborator)
  removeCollaborator(ctx: StateContext<LoadManagementStateModel>, { collaboratorId }: RemoveCollaborator) {
    const { jobFile } = ctx.getState();
    if (!jobFile) {
      return ctx.dispatch(new SetNotificationAction('Job file not found', AlertDialogType.ERROR));
    }

    return this.jobFileService.removeCollaborator(jobFile.orgId, jobFile.jobRefId, collaboratorId).pipe(
      tap((updatedJobFile: JobFile) => {
        // Avoid mutating the original (readonly) jobFile object, create a shallow copy with updated collaborators
        const updatedJobFileCopy = { ...jobFile, collaborators: updatedJobFile.collaborators };
        ctx.patchState({ jobFile: updatedJobFileCopy });
        ctx.dispatch(new SetNotificationAction('Collaborator removed successfully', AlertDialogType.SUCCESS));
      }),
      catchError(error => {
        console.log(error);
        ctx.dispatch(new SetNotificationAction('Failed to remove collaborator', AlertDialogType.ERROR));
        return EMPTY;
      })
    );
  }

  @Action(OptimizeContainers)
  optimizeContainers(ctx: StateContext<LoadManagementStateModel>, { orgId, jobRefId, containerIds, useCloudRunSync }: OptimizeContainers) {
    return this.loadManagementService.optimizeContainers(orgId, jobRefId, containerIds, useCloudRunSync).pipe(
      tap(() => {
        const message = containerIds.length === 1
          ? 'Container route optimized successfully'
          : `${containerIds.length} container routes optimized successfully`;
        ctx.dispatch(new SetNotificationAction(message, AlertDialogType.SUCCESS));
      }),
      catchError(error => {
        ctx.dispatch(new SetNotificationAction('Error optimizing container routes', AlertDialogType.ERROR));
        return EMPTY;
      })
    );
  }

  @Action(UpdateContainerFcl)
  updateContainerFcl(
    { dispatch }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, containerId, payload }: UpdateContainerFcl
  ) {
    return this.loadManagementService.updateContainerFcl(orgId, jobRefId, containerId, payload).pipe(
      tap(() => {
        dispatch(new SetNotificationAction('Container details updated successfully', AlertDialogType.SUCCESS));
        dispatch(new LoadJobFile(orgId, jobRefId));
      }),
      catchError(error => {
        dispatch(new SetNotificationAction('Failed to update container details', AlertDialogType.ERROR));
        return EMPTY;
      })
    );
  }

  @Action(UpdateContainerVehicleCategory)
  updateContainerVehicleCategory(
    { dispatch }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, containerId, vehicleCategoryId }: UpdateContainerVehicleCategory
  ) {
    return this.loadManagementService.addVehicleDataToContainer(orgId, jobRefId, containerId, vehicleCategoryId).pipe(
      tap(() => {
        dispatch(new SetNotificationAction('Container details updated successfully', AlertDialogType.SUCCESS));
        dispatch(new LoadJobFile(orgId, jobRefId));
      }),
      catchError(error => {
        dispatch(new SetNotificationAction('Failed to update container details', AlertDialogType.ERROR));
        return EMPTY;
      })
    );
  }

  @Action(LoadOperationLabels)
  loadOperationLabels(
    { getState, patchState }: StateContext<LoadManagementStateModel>,
    { operationId, force }: LoadOperationLabels
  ) {
    if (!operationId) {
      patchState({ operationLabels: [], operationLabelsOperationId: null });
      return EMPTY;
    }
    const cached = getState().operationLabelsOperationId === operationId;
    if (cached && !force) {
      return EMPTY;
    }
    // A forced refresh keeps the current labels on screen until the new ones arrive.
    patchState(cached ? {} : { operationLabels: [], operationLabelsOperationId: operationId });
    return this.operationLabelService.getLabels(operationId, true, false).pipe(
      tap(labels => {
        if (getState().operationLabelsOperationId === operationId) {
          patchState({ operationLabels: labels || [] });
        }
      }),
      catchError(() => {
        patchState({ operationLabelsOperationId: null });
        return EMPTY;
      })
    );
  }

  @Action(LoadInvoicedLoadIds)
  loadInvoicedLoadIds(
    { patchState }: StateContext<LoadManagementStateModel>,
    { loadIds }: LoadInvoicedLoadIds
  ) {
    if (!loadIds?.length) {
      patchState({ invoicedLoadIds: [] });
      return EMPTY;
    }
    return this.operationLabelService.getInvoicedLoadIds(loadIds).pipe(
      tap(invoiced => patchState({ invoicedLoadIds: invoiced || [] })),
      catchError(() => EMPTY)
    );
  }

  @Action(UpdateContainerLabels)
  updateContainerLabels(
    { dispatch }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, containerId, labelIds }: UpdateContainerLabels
  ) {
    return this.operationLabelService.updateContainerLabels(orgId, jobRefId, containerId, labelIds).pipe(
      tap(() => dispatch(new LoadJobFile(orgId, jobRefId)))
    );
  }

  @Action(UpdateContainerCustomsChannel)
  updateContainerCustomsChannel(
    { dispatch }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, containerId, customsChannel }: UpdateContainerCustomsChannel
  ) {
    return this.loadManagementService.updateCustomsChannel(orgId, jobRefId, containerId, customsChannel).pipe(
      tap(() => {
        dispatch(new LoadJobFile(orgId, jobRefId));
      }),
      catchError(error => {
        dispatch(new SetNotificationAction('Failed to update customs channel', AlertDialogType.ERROR));
        return EMPTY;
      })
    );
  }

  @Action(UpdateLoadKeyDetails)
  updateLoadKeyDetails(
    { dispatch }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, containerId, loadKeyDetails }: UpdateLoadKeyDetails
  ) {
    return this.loadManagementService.updateLoadKeyDetails(orgId, jobRefId, containerId, loadKeyDetails).pipe(
      tap(() => {
        dispatch(new LoadJobFile(orgId, jobRefId));
      }),
      catchError(error => {
        dispatch(new SetNotificationAction('Failed to update key dates and shipment method', AlertDialogType.ERROR));
        return EMPTY;
      })
    );
  }

  @Action(SaveRemark)
  saveRemark({dispatch}: StateContext<LoadManagementStateModel>, {orgId, jobRefId, containerId, remark}: SaveRemark) {
    return this.jobFileService.saveRemark(orgId, jobRefId, containerId, remark).pipe(
      tap(() => {
        dispatch(new LoadJobFile(orgId, jobRefId));
      }),
      mergeMap(() => dispatch(new SetNotificationAction('Successfully added remark', AlertDialogType.SUCCESS))),
      catchError(() => dispatch(new SetNotificationAction('Error adding remark', AlertDialogType.ERROR)))
    );
  }

  @Action(SaveRemarks)
  saveRemarks({dispatch}: StateContext<LoadManagementStateModel>, {orgId, jobRefId, containerId, remarks}: SaveRemarks) {
    return this.jobFileService.saveRemarks(orgId, jobRefId, containerId, remarks).pipe(
      tap(() => {
        dispatch(new LoadJobFile(orgId, jobRefId));
      }),
      mergeMap(() => dispatch(new SetNotificationAction('Successfully added remarks', AlertDialogType.SUCCESS))),
      catchError(() => dispatch(new SetNotificationAction('Error adding remarks', AlertDialogType.ERROR)))
    );
  }

  @Action(DeleteRemark)
  deleteRemark({dispatch}: StateContext<LoadManagementStateModel>, {orgId, jobRefId, containerId, index}: DeleteRemark) {
    return this.jobFileService.deleteRemark(orgId, jobRefId, containerId, [index]).pipe(
      tap(() => {
        dispatch(new LoadJobFile(orgId, jobRefId));
      }),
      mergeMap(() => dispatch(new SetNotificationAction('Successfully deleted remark', AlertDialogType.SUCCESS))),
      catchError(() => dispatch(new SetNotificationAction('Error deleting remark', AlertDialogType.ERROR)))
    );
  }

  @Action(LoadRemarks)
  loadRemarks({dispatch}: StateContext<LoadManagementStateModel>, {orgId, jobRefId, containerId}: LoadRemarks) {
    return this.jobFileService.getRemarks(orgId, jobRefId, containerId).pipe(
      catchError(() => dispatch(new SetNotificationAction('Error loading remarks', AlertDialogType.ERROR)))
    );
  }

  @Action(SaveWaypoints)
  saveWaypoints({patchState, getState}: StateContext<LoadManagementStateModel>, {containerId, undoStack, redoStack}: SaveWaypoints) {
    patchState({
      undoWaypoints: {...getState().undoWaypoints, [containerId]: undoStack},
      redoWaypoints: {...getState().redoWaypoints, [containerId]: redoStack}
    });
  }

  @Action(UpdateContainerRouteWaypoints)
  updateContainerRouteWaypoints(
    { dispatch, patchState, getState }: StateContext<LoadManagementStateModel>,
    { orgId, jobRefId, container }: UpdateContainerRouteWaypoints
  ) {
    return this.loadManagementService.updateContainerRouteWaypoints(orgId, jobRefId, container).pipe(
      tap((savedContainer) => {
        // The response legs carry the distance / travelingTime recalculated through the new
        // waypoints; the request payload still holds the pre-drag numbers, so never fall back to
        // it while a response is available.
        const updatedContainer = savedContainer?.containerId ? savedContainer : container;
        const state = getState();
        const jobFile = state.jobFile;
        const containersMap: EntityMap<string, AddContainer> = {
          ...state.containers,
          [container.containerId]: updatedContainer
        };
        // Shared / chained jobs render their load cards from rootLoads instead of jobFile.containers.
        const rootLoadIdx = state.rootLoads?.findIndex(c => c.containerId === container.containerId) ?? -1;
        const nextRootLoads = rootLoadIdx >= 0 ? [...state.rootLoads] : state.rootLoads;
        if (rootLoadIdx >= 0) {
          nextRootLoads[rootLoadIdx] = applyWaypointLegsFromAddContainer(state.rootLoads[rootLoadIdx], updatedContainer);
        }
        const idx = jobFile?.containers?.length
          ? jobFile.containers.findIndex(c => c.containerId === container.containerId)
          : -1;
        if (idx < 0) {
          patchState({ containers: containersMap, rootLoads: nextRootLoads });
          return;
        }
        const merged = applyWaypointLegsFromAddContainer(jobFile.containers[idx], updatedContainer);
        const nextJobContainers = [...jobFile.containers];
        nextJobContainers[idx] = merged;
        patchState({
          jobFile: { ...jobFile, containers: nextJobContainers },
          containers: containersMap,
          rootLoads: nextRootLoads
        });
      }),
      map(() => undefined),
      catchError(() => dispatch(new SetNotificationAction('Error updating waypoints', AlertDialogType.ERROR)))
    );
  }

  @Action(UpdateContainer)
  updateContainer({dispatch, patchState, getState}: StateContext<LoadManagementStateModel>, {container}: UpdateContainer) {
    const orgId = this.store.selectSnapshot(AuthState.getOrgId);
    const jobFile = this.store.selectSnapshot(LoadManagementState.jobFile);
    
    if (!orgId || !jobFile?.jobRefId) {
      dispatch(new SetNotificationAction('Missing required information to update container', AlertDialogType.ERROR));
      return EMPTY;
    }

    return this.loadManagementService.updateContainer(orgId, jobFile.jobRefId, container)
      .pipe(
        tap(() => {
          // Update container in local state
          const containers = { ...getState().containers };
          containers[container.containerId] = container;
          patchState({ containers });
          
          dispatch(new SetNotificationAction('Container updated successfully', AlertDialogType.SUCCESS));
        }),
        catchError(err => {
          dispatch(new SetNotificationAction('Error updating container', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(FindInitContainersByContainerIds)
  findInitContainersByContainerIds({dispatch, patchState}: StateContext<LoadManagementStateModel>,
                                   {orgId, jobRefId, containerIds}: FindInitContainersByContainerIds) {
    return this.loadManagementService.findInitContainersByContainerIds(orgId, jobRefId, containerIds)
      .pipe(
        tap((initContainers) => {
          patchState({ rootLoads: initContainers });
        }),
        catchError(err => {
          dispatch(new SetNotificationAction('Error finding init containers', AlertDialogType.ERROR));
          return EMPTY;
        })
      );
  }

  @Action(SaveOtherReferenceNumber)
  saveOtherReferenceNumber(
    {dispatch}: StateContext<LoadManagementStateModel>,
    {orgId, jobRefId, containerId, locationId, serviceId, refNumbers, workOrderId}: SaveOtherReferenceNumber
  ) {
    const workOrderIdValue = workOrderId || 'wizard';
    return this.jobFileService.saveOtherReferenceNumber(orgId, jobRefId, containerId, locationId, serviceId, workOrderIdValue, refNumbers).pipe(
      tap(() => {
        dispatch(new LoadJobFile(orgId, jobRefId));
        dispatch(new GetContainer(orgId, jobRefId, containerId));
      }),
      mergeMap(() => dispatch(new SetNotificationAction('Successfully added other reference number', AlertDialogType.SUCCESS))),
      catchError(() => dispatch(new SetNotificationAction('Error adding other reference number', AlertDialogType.ERROR)))
    );
  }

  @Action(DeleteOtherReferenceNumber)
  deleteOtherReferenceNumber(
    {dispatch}: StateContext<LoadManagementStateModel>,
    {orgId, jobRefId, containerId, locationId, serviceId, refNumber, workOrderId}: DeleteOtherReferenceNumber
  ) {
    if (refNumber && refNumber.length > 0) {
      const workOrderIdValue = workOrderId || 'wizard';
      return this.jobFileService.deleteOtherReferenceNumber(orgId, jobRefId, containerId, locationId, serviceId, workOrderIdValue, refNumber).pipe(
        tap(() => {
          dispatch(new LoadJobFile(orgId, jobRefId));
          dispatch(new GetContainer(orgId, jobRefId, containerId));
        }),
        mergeMap(() => dispatch(new SetNotificationAction('Successfully deleted other reference number', AlertDialogType.SUCCESS))),
        catchError(() => dispatch(new SetNotificationAction('Error deleting other reference number', AlertDialogType.ERROR)))
      );
    }
    return EMPTY;
  }
}

/**
 * Merge InitLeg waypoints / encoded path from AddContainer into the job-file container used by the map.
 *
 * distance and travelingTime come along because the load cards and the route-setup stats card read
 * them off the job-file container; leaving them behind keeps the pre-drag totals on screen.
 */
function applyWaypointLegsFromAddContainer(
  jobFileContainer: JobFileContainer,
  addContainer: AddContainer
): JobFileContainer {
  const srcLegs = addContainer?.legs;
  if (!srcLegs?.length || !jobFileContainer.legs?.length) {
    return jobFileContainer;
  }
  const merged: JobFileContainer = {
    ...jobFileContainer,
    legs: jobFileContainer.legs.map((leg, i) => {
      const src = srcLegs[i] as {
        wayPoints?: typeof leg.wayPoints;
        encodedPath?: string;
        encodedPathId?: string;
        distance?: number;
        travelingTime?: number;
        startTimeEta?: Date;
        endTimeEta?: Date;
      } | undefined;
      if (!src) {
        return leg;
      }
      return {
        ...leg,
        ...(src.wayPoints != null ? { wayPoints: src.wayPoints } : {}),
        ...(src.encodedPath != null ? { encodedPath: src.encodedPath, encodedPathId: src.encodedPathId } : {}),
        ...(src.distance != null ? { distance: src.distance } : {}),
        ...(src.travelingTime != null ? { travelingTime: src.travelingTime } : {}),
        ...(src.startTimeEta != null ? { startTimeEta: src.startTimeEta } : {}),
        ...(src.endTimeEta != null ? { endTimeEta: src.endTimeEta } : {})
      } as JobFileContainer['legs'][number];
    })
  };

  // The backend shifts the service schedule by the change in travel time, so the panel's Required
  // Time of Arrival / Leave and Positioning Time come back on the response. They arrive flattened,
  // while the job-file container keeps them nested per location, so they are matched by service id.
  const srcServicesById = new Map(
    (addContainer.services ?? []).filter(service => service?.id).map(service => [service.id, service])
  );
  if (srcServicesById.size && merged.services?.length) {
    merged.services = merged.services.map(locationService => ({
      ...locationService,
      services: (locationService.services ?? []).map(service => {
        const src = srcServicesById.get(service?.id);
        if (!src) {
          return service;
        }
        return {
          ...service,
          ...(src.plannedStartDateTime != null ? { plannedStartDateTime: src.plannedStartDateTime } : {}),
          ...(src.plannedTimeToLeave != null ? { plannedTimeToLeave: src.plannedTimeToLeave } : {}),
          ...(src.positioningTime != null ? { positioningTime: src.positioningTime } : {}),
          ...(src.travelTime != null ? { travelTime: src.travelTime } : {})
        };
      })
    }));
  }

  // Distribution loads read their totals off shipmentLoadDetails instead of the legs.
  const srcShipmentLoadDetails = addContainer.shipmentLoadDetails;
  if (srcShipmentLoadDetails && merged.shipmentLoadDetails) {
    merged.shipmentLoadDetails = {
      ...merged.shipmentLoadDetails,
      totalDistance: srcShipmentLoadDetails.totalDistance ?? merged.shipmentLoadDetails.totalDistance,
      totalTime: srcShipmentLoadDetails.totalTime ?? merged.shipmentLoadDetails.totalTime
    };
  }

  return merged;
}

function calculateShipmentMetrics(shipmentOrderSpecs: ShipmentOrderSpec[]) {
  return shipmentOrderSpecs.reduce<{
    totalOrderSpecs: number;
    totalCapacity: number;
    totalWeight: number;
  }>((acc, spec) => {
    if (!spec) {
      return acc;
    }

    acc.totalOrderSpecs += 1;

    (spec.shipmentOrders ?? []).forEach(order => {
      const quantity = order?.quantity ?? 0;
      acc.totalCapacity += (order?.cbm ?? 0) * quantity;
      acc.totalWeight += (order?.weight ?? 0) * quantity;
    });

    return acc;
  }, {
    totalOrderSpecs: 0,
    totalCapacity: 0,
    totalWeight: 0
  });
}

export function aggregateSummaryMetrics(containers: JobFileContainer[]): LoadManagementSummaryMetrics {
  const pickupLocations = new Set<string>();
  const deliveryLocations = new Set<string>();

  const summary = containers.reduce<LoadManagementSummaryMetrics>((acc, container) => {
    const details = container.shipmentLoadDetails;
    if (!details) {
      // Use vehicleData when shipmentLoadDetails is not available, matching component logic
      acc.plannedCapacity += container.vehicleData?.maxLoadVolume ?? 0;
      acc.plannedWeight += container.vehicleData?.maxLoadWeight ?? 0;
      return acc;
    }

    acc.totalTruckLoads += 1;
    acc.totalPlannedDistanceKm += convertDistanceToKm(details.totalDistance);
    acc.plannedCapacity += details.requiredVehicleCapacity ?? 0;
    acc.plannedWeight += details.requiredVehicleWeight ?? 0;

    if (details.startLocation) {
      pickupLocations.add(details.startLocation);
    }
    (details.intermediateLocations ?? []).forEach(locationId => {
      if (locationId) {
        deliveryLocations.add(locationId);
      }
    });
    if (details.endLocation) {
      deliveryLocations.add(details.endLocation);
    }

    const shipmentMetrics = calculateShipmentMetrics(details.shipmentOrderSpecs ?? []);
    acc.totalOrders += shipmentMetrics.totalOrderSpecs;
    acc.totalShipments += details.intermediateLocations?.length ?? 0;
    acc.carryingCapacity += shipmentMetrics.totalCapacity;
    acc.carryingWeight += shipmentMetrics.totalWeight;

    return acc;
  }, {
    totalOrders: 0,
    totalShipments: 0,
    totalPickupLocations: 0,
    totalDeliveryLocations: 0,
    totalTruckLoads: 0,
    totalPlannedDistanceKm: 0,
    plannedCapacity: 0,
    carryingCapacity: 0,
    plannedWeight: 0,
    carryingWeight: 0
  });

  summary.totalPickupLocations = pickupLocations.size;
  summary.totalDeliveryLocations = deliveryLocations.size;

  return summary;
}





export function formatDecimal(value?: number, fractionDigits = 2): string {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return '0';
  }
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });
}

function safePercentage(value: number, total: number): number {
  if (!total || total <= 0) {
    return 0;
  }
  const percentage = (value / total) * 100;
  if (Number.isNaN(percentage) || !Number.isFinite(percentage)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Number(percentage.toFixed(1))));
}

function getUtilizationColor(value: number): string {
  if (value >= 75) {
    return '#14B961';
  }

  if (value >= 50) {
    return '#F9B516';
  }

  if (value >= 25) {
    return '#F77F2B';
  }

  return '#F55C5C';
}


function convertDistanceToKm(distance?: number): number {
  if (!distance || distance <= 0) {
    return 0;
  }

  // Distances from backend are typically in meters. If value looks like kilometers already,
  // avoid double conversion.
  if (distance > 1000) {
    return distance / 1000;
  }

  return distance;
}

